import { FC, useEffect, useState, useMemo } from 'react';
import { AdministrationViewProps } from 'src/apis/administration/administration.interfaces';
import { Grid } from '@mui/material';
import { pages } from '../../../utils/common';
import {
  useAdministrationTabMenuItems,
  useAdministrationAvailableRouteIds,
} from './administration.helpers';
import MainTitle from '../../components/mainTitle';
import TabGroup from '../../components/tabGroup';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from '../../../utils/CONSTS';
import NavigationDrawer from '../../components/navigationDrawer/navigationDrawer';
import { RoutesAndScheduleProvider } from './contexts/routesAndSchedule.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useRbacState } from 'src/context/rbac.context';
import { useRbacSafeLandingPath } from 'src/hooks/useRbacSafeLandingPath';
import { hasAnySubmoduleReadPermission } from 'src/utils/rbacHelpers';

const AdministrationContent: FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { administrationSubRouteId } = useParams();
  const navigate = useNavigate();
  const { translateText } = useTranslation();
  const { rbac } = useRbacState();
  const safeLanding = useRbacSafeLandingPath();
  const availableRouteIds = useAdministrationAvailableRouteIds();
  const tabs = useAdministrationTabMenuItems(translateText);

  const hasAccess = useMemo(
    () => hasAnySubmoduleReadPermission(rbac, 'administration'),
    [rbac]
  );

  useEffect(() => {
    if (availableRouteIds.length === 0) return;

    const routeId =
      administrationSubRouteId && administrationSubRouteId !== 'undefined'
        ? administrationSubRouteId
        : availableRouteIds[0];

    if (!availableRouteIds.includes(routeId)) {
      navigate(pages.administration.path + '/' + availableRouteIds[0], {
        replace: true,
      });
      return;
    }

    const tabIndex = availableRouteIds.indexOf(routeId);
    setSelectedTab(tabIndex >= 0 ? tabIndex : 0);

    if (!administrationSubRouteId || administrationSubRouteId === 'undefined') {
      navigate(pages.administration.path + '/' + routeId, { replace: true });
    }
  }, [administrationSubRouteId, navigate, availableRouteIds]);

  if (!hasAccess) {
    return <Navigate to={safeLanding} replace />;
  }

  if (availableRouteIds.length === 0) {
    return <Navigate to={safeLanding} replace />;
  }

  const locationChangeHandler = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < availableRouteIds.length) {
      navigate(
        pages.administration.path + '/' + availableRouteIds[tabIndex],
        { replace: true }
      );
    }
  };

  return (
    <RoutesAndScheduleProvider>
      <Grid
        container
        data-testid="administration-container"
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          px: { xs: 0, sm: 0 },
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            pr: { xs: 0, sm: 4 },
          }}
          data-testid="administration-title-section"
        >
          <MainTitle
            title={translateText('administration.title')}
            subTitle={selectedTab ? tabs[selectedTab].label : ''}
          />
        </Grid>
        <Grid
          item
          xs={12}
          data-testid="administration-tabs-section"
          sx={{
            width: '100%',
            height: 'calc(100% - 64px)',
            overflow: 'hidden',
            px: { xs: 0, sm: 0 },
          }}
        >
          <TabGroup
            key={selectedTab}
            tabs={tabs}
            initiallySelected={selectedTab}
            onTabChange={(value) => locationChangeHandler(value)}
            dataTestIdPrefix="administration"
          />
        </Grid>
      </Grid>
    </RoutesAndScheduleProvider>
  );
};

// Main component that provides navigation context
const AdministrationView: FC<AdministrationViewProps> = () => {
  return (
    <NavigationDrawer
      selectedPage={pages.administration.path}
      window={window ?? null}
    >
      <Helmet>
        <title>{CONSTANTS.title.administration}</title>
      </Helmet>
      <AdministrationContent />
    </NavigationDrawer>
  );
};

export default AdministrationView;
